import React, { useEffect, useRef } from 'react'
import * as THREE from 'three'
import worldMapTexture from '../assets/images/world-map.png'

const CAPE_TOWN = { lat: -33.9249, lon: 18.4241 }
const HYDERABAD = { lat: 17.3850, lon: 78.4867 }

function latLongToVector3(lat, lon, radius) {
  const phi = (90 - lat) * (Math.PI / 180)
  const theta = (lon + 180) * (Math.PI / 180)
  const x = -radius * Math.sin(phi) * Math.cos(theta)
  const z = radius * Math.sin(phi) * Math.sin(theta)
  const y = radius * Math.cos(phi)
  return new THREE.Vector3(x, y, z)
}

export default function Globe() {
  const stageRef = useRef(null)
  const labelCTRef = useRef(null)
  const labelHYRef = useRef(null)
  // A ref, not state — the animation loop reads it every frame and this must
  // not re-run the effect or rebuild the scene.
  const hoveredRef = useRef(false)

  useEffect(() => {
    const stage = stageRef.current
    let W = stage.clientWidth
    let H = stage.clientHeight

    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(45, W / H, 0.1, 1000)
    camera.position.set(0, 0.4, 7.5)

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
    renderer.setSize(W, H)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.domElement.style.display = 'block'
    stage.appendChild(renderer.domElement)

    const globeGroup = new THREE.Group()
    scene.add(globeGroup)

    const textureLoader = new THREE.TextureLoader()
    const worldTexture = textureLoader.load(worldMapTexture)
    worldTexture.colorSpace = THREE.SRGBColorSpace

    const sphereGeo = new THREE.SphereGeometry(2.4, 64, 48)
    const mapMat = new THREE.MeshBasicMaterial({ map: worldTexture, transparent: true })
    const mapSphere = new THREE.Mesh(sphereGeo, mapMat)
    globeGroup.add(mapSphere)

    const innerGeo = new THREE.SphereGeometry(2.38, 32, 24)
    const innerMat = new THREE.MeshBasicMaterial({ color: 0x010528 })
    const innerSphere = new THREE.Mesh(innerGeo, innerMat)
    globeGroup.add(innerSphere)

    const rimMat = new THREE.MeshBasicMaterial({ color: 0x0AB3D2, transparent: true, opacity: 0.05, side: THREE.BackSide })
    const rim = new THREE.Mesh(new THREE.SphereGeometry(2.62, 32, 24), rimMat)
    globeGroup.add(rim)

    const ctPos = latLongToVector3(CAPE_TOWN.lat, CAPE_TOWN.lon, 2.4)
    const hyPos = latLongToVector3(HYDERABAD.lat, HYDERABAD.lon, 2.4)

    function makeMarker(pos, color) {
      const geo = new THREE.SphereGeometry(0.045, 16, 16)
      const mat = new THREE.MeshBasicMaterial({ color })
      const m = new THREE.Mesh(geo, mat)
      m.position.copy(pos)
      globeGroup.add(m)

      const glowGeo = new THREE.SphereGeometry(0.09, 16, 16)
      const glowMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.3 })
      const glow = new THREE.Mesh(glowGeo, glowMat)
      glow.position.copy(pos)
      globeGroup.add(glow)
      return m
    }
    makeMarker(ctPos, 0x0AB3D2)
    makeMarker(hyPos, 0xE8A93D)

    function makeArc(p1, p2) {
      const mid = p1.clone().add(p2).multiplyScalar(0.5)
      mid.normalize().multiplyScalar(2.4 + p1.distanceTo(p2) * 0.35)
      const curve = new THREE.QuadraticBezierCurve3(p1, mid, p2)
      const points = curve.getPoints(80)
      const geo = new THREE.BufferGeometry().setFromPoints(points)
      const mat = new THREE.LineBasicMaterial({ color: 0x0AB3D2, transparent: true, opacity: 0.55 })
      const line = new THREE.Line(geo, mat)
      globeGroup.add(line)
      return curve
    }
    const arcCurve = makeArc(ctPos, hyPos)

    const pulseGeo = new THREE.SphereGeometry(0.05, 12, 12)
    const pulseMat = new THREE.MeshBasicMaterial({ color: 0xFFFFFF })
    const pulse = new THREE.Mesh(pulseGeo, pulseMat)
    globeGroup.add(pulse)

    let pulseT = 0

    function angleToFaceCamera(p) {
      return Math.atan2(-p.x, p.z)
    }

    globeGroup.rotation.y = angleToFaceCamera(ctPos)

    const REVERSE_DIRECTION = true
    const ROTATE_SPEED = 0.014

    function updateLabel(el, worldPos) {
      if (!el) return
      const vector = worldPos.clone().project(camera)
      const x = (vector.x * 0.5 + 0.5) * W
      const y = (-vector.y * 0.5 + 0.5) * H

      const worldNormal = worldPos.clone().normalize()
      const camDir = camera.position.clone().normalize()
      const facing = worldNormal.dot(camDir)

      el.style.left = x + 'px'
      el.style.top = y + 'px'
      el.style.opacity = facing > 0.15 ? '1' : '0'
    }

    let frameId
    function animate() {
      frameId = requestAnimationFrame(animate)

      // Hovering holds the globe still so labels and markers can be read.
      // The render loop keeps running, so the arc pulse continues.
      if (!hoveredRef.current) {
        globeGroup.rotation.y += (REVERSE_DIRECTION ? -1 : 1) * ROTATE_SPEED
      }

      pulseT += 0.004
      if (pulseT > 1) pulseT = 0
      const p = arcCurve.getPoint(pulseT)
      pulse.position.copy(p)

      globeGroup.updateMatrixWorld()
      updateLabel(labelCTRef.current, ctPos.clone().applyMatrix4(globeGroup.matrixWorld))
      updateLabel(labelHYRef.current, hyPos.clone().applyMatrix4(globeGroup.matrixWorld))

      renderer.render(scene, camera)
    }
    frameId = requestAnimationFrame(animate)

    const onPointerEnter = () => { hoveredRef.current = true }
    const onPointerLeave = () => { hoveredRef.current = false }
    stage.addEventListener('mouseenter', onPointerEnter)
    stage.addEventListener('mouseleave', onPointerLeave)

    const resizeObserver = new ResizeObserver(() => {
      W = stage.clientWidth
      H = stage.clientHeight
      camera.aspect = W / H
      camera.updateProjectionMatrix()
      renderer.setSize(W, H)
    })
    resizeObserver.observe(stage)

    return () => {
      cancelAnimationFrame(frameId)
      stage.removeEventListener('mouseenter', onPointerEnter)
      stage.removeEventListener('mouseleave', onPointerLeave)
      resizeObserver.disconnect()
      stage.removeChild(renderer.domElement)
      sphereGeo.dispose()
      mapMat.dispose()
      worldTexture.dispose()
      innerGeo.dispose()
      innerMat.dispose()
      rimMat.dispose()
      pulseGeo.dispose()
      pulseMat.dispose()
      renderer.dispose()
    }
  }, [])

  return (
    <div className="globe-stage" ref={stageRef}>
      <div className="marker-label" ref={labelCTRef}>Cape Town<span className="sub">Headquarters</span></div>
      <div className="marker-label" ref={labelHYRef}>India<span className="sub">Satellite Office</span></div>
    </div>
  )
}
